import * as Sentry from "@sentry/browser";

export interface VaultSentryConfigOptions {
  vaultVersion: string;
  vaultGitCommitHash: string;
  sentryEnvironment: string;
  sentryDsn: string;
  sentryTunnelUrl: string;
}

/**
 * Simple wrapper around the Sentry client.
 * A place for useful abstractions specific to Vault's
 * Sentry use case.
 */
export class VaultSentry {
  public static get client() {
    if (!VaultSentry.isInitialized) {
      throw new Error(
        "Error: tried to access Sentry client before it was initialized.",
      );
    }
    return Sentry;
  }

  // protected write, public read
  protected static initialized: boolean = false;
  public static get isInitialized(): boolean {
    return VaultSentry.initialized;
  }

  // configure the sentry client
  public static initialize(options: VaultSentryConfigOptions): void {
    Sentry.init({
      dsn: options.sentryDsn,
      environment: options.sentryEnvironment,
      tunnel: options.sentryTunnelUrl,
      integrations: [
        // Capture Console Sentry Integration:
        // will automatically take anything that is logged to console.error
        // and forward it to Sentry as an error alert. For now, console.error
        // is a quick, simple solution since it requires no logger dependencies
        // and error titles will always match the literal error message you
        // are looking for when trying to reproduce or trace the issue.
        Sentry.captureConsoleIntegration({ levels: ["error"] }),
      ],
      release: `vault@${options.vaultVersion}-${options.vaultGitCommitHash}`,
      tracesSampleRate: 1.0,
      maxBreadcrumbs: 50,
    });
    // mark initialized.
    VaultSentry.initialized = true;
    console.info("Sentry client registered successfully.");
  }
}
